import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, switchMap } from 'rxjs';
import {
    isEqual as _isEqual
} from 'lodash-es';

import { QueryParamsChanged, UpdateQueryParams } from './query-params.actions';
import { removeInvalidQueryParamsKeysFromObject, validateAndUpdateQueryParamContent } from './query-params.utils';

import { GameState } from 'src/app/states/game/game.state';
import { deepDistinctUntilChanged, isDefined } from 'src/app/utils/operator.utils';

/**
 * A services that listens for changes in the query params and updates them if necessary when they become invalid
 */
@Injectable({ providedIn: 'root' })
export class QueryParamsService {
    private readonly _store = inject(Store);
    private readonly _route = inject(ActivatedRoute);
    private readonly _router = inject(Router);
    private readonly _actions = inject(Actions);

    constructor() {
        this._router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            switchMap(() => {
                return combineLatest([
                    this._route.queryParams.pipe(deepDistinctUntilChanged()),
                    this._store.select(GameState.selectors.game).pipe(isDefined()),
                    this._store.select(GameState.selectors.vidParams).pipe(isDefined())
                ]);
            }),
            takeUntilDestroyed()
        ).subscribe(([params, game, vidParams]) => {
            // create valid params by removing any invalid param keys and making sure that the values of the params make sense
            // based on the data already fetched
            const validParams = validateAndUpdateQueryParamContent(
                removeInvalidQueryParamsKeysFromObject(params),
                game,
                vidParams
            );

            if (!_isEqual(validParams, params)) {
                // the params from the browser were not valid and so we need to update them with the valid ones we have created
                this._router.navigate(
                    [],
                    {
                        relativeTo: this._route,
                        queryParams: validParams,
                        replaceUrl: true
                    }
                ).catch(error => {
                    console.error(error);
                });
            } else {
                // the params were valid so we dispatch the action to update the state properties
                this._store.dispatch(new QueryParamsChanged(params));
            };
        });

        this._actions.pipe(
            ofActionDispatched(UpdateQueryParams),
            map(a => a.queryParams)
        ).subscribe(newParams => {
            const validParams = removeInvalidQueryParamsKeysFromObject(newParams);
            this._router.navigate(
                [],
                {
                    relativeTo: this._route,
                    queryParams: validParams,
                    replaceUrl: true,
                    queryParamsHandling: 'merge'
                }
            ).catch(error => {
                console.error(error);
            });
        });
    }
}
